"use client";

import { type HTMLAttributes, useEffect, useRef, useMemo } from "react";
import { motion, useAnimationControls } from "motion/react";
import { mergeClasses } from "@huub-web/utils";

import type { ChildrenProp, ClassNameProp } from "../../props";
import {
  marqueeContainerStyle,
  marqueeContentStyle,
  marqueeStyle,
  marqueeAnimationStyle,
} from "./Marquee.css";
import type { RecipeVariants } from "@vanilla-extract/recipes";

export type MarqueeProps = HTMLAttributes<HTMLDivElement> &
  ChildrenProp &
  ClassNameProp &
  RecipeVariants<typeof marqueeContentStyle> & {
    /**
     * The duration in seconds for one complete animation cycle
     * @default 20
     */
    duration?: number;
    /**
     * The direction of the marquee animation
     * @default "left"
     */
    direction?: "left" | "right";
    /**
     * Whether to pause the animation on hover
     * @default false
     */
    pauseOnHover?: boolean;
    /**
     * Number of duplicate content blocks for seamless looping
     * @default 2
     */
    duplicates?: number;
  };

/**
 * Marquee component that animates its children horizontally using Framer Motion.
 * Creates a smooth scrolling effect where content moves continuously from one side to the other.
 */
export const Marquee = ({
  children,
  className,
  duration = 20,
  direction = "left",
  pauseOnHover = false,
  duplicates = 2,
  gap = "m",
  align = "center",
  ...props
}: MarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();

  // Calculate the percentage to move based on the number of content blocks
  // We want to move exactly one content block width to create seamless loop
  const movePercentage = 100 / (duplicates + 1);

  const animationConfig = useMemo(
    () => ({
      x:
        direction === "left"
          ? [`0%`, `-${movePercentage}%`]
          : [`0%`, `${movePercentage}%`],
      transition: {
        repeat: Infinity,
        repeatType: "loop" as const,
        duration: duration,
        ease: "linear" as const,
      },
    }),
    [direction, movePercentage, duration],
  );

  const pauseAnimation = () => {
    controls.stop();
  };

  const resumeAnimation = () => {
    controls.start(animationConfig);
  };

  useEffect(() => {
    controls.start(animationConfig);
  }, [controls, animationConfig]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: mouse events control hover-pause animation only
    <div
      ref={containerRef}
      className={mergeClasses(
        marqueeStyle,
        marqueeContainerStyle({ gap, align }),
        className,
      )}
      onMouseEnter={pauseOnHover ? pauseAnimation : undefined}
      onMouseLeave={pauseOnHover ? resumeAnimation : undefined}
      {...props}
    >
      <motion.div
        className={marqueeContentStyle({ gap, align })}
        animate={controls}
        style={{
          display: "flex",
          alignItems: align,
          minWidth: "max-content",
          willChange: "transform",
        }}
      >
        <div className={marqueeAnimationStyle({ align, gap })}>{children}</div>
        {Array.from({ length: duplicates }, (_, index) => (
          <div
            key={`duplicate-${index}`}
            className={marqueeAnimationStyle({ align, gap })}
            aria-hidden="true"
          >
            {children}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

Marquee.displayName = "Marquee";
